import { useCallback, useEffect, useRef, useState } from 'react';
import { AlertManager } from '@creditflowresearch/uilib/Alert';
import Logger from '@framework/sentry/Logger';
import { MIN_QUERY_LENGTH } from '@/creditflowresearch/application/BondScreener/constants';
import { getContainer } from '@framework/di/getContainer';
import BondScreenersRepository
  from '@/creditflowresearch/infrastructure/repository/BondScreenersRepository';
import {
  useCorporateBondScreenerSidebarFilterContext,
} from '@/creditflowresearch/application/CorporateBondScreener/Sidebar/useCorporateBondScreenerSidebarFilterContext';
import type { IBondScreenerCompanyGroup, TFilterPayload } from '@/creditflowresearch/domain/vo/Bondscreener';
import useExcludeToggle from '@/creditflowresearch/ui/common/hooks/sidebar/useExcludeToggle';
import { ICompanyOption, toOptions } from './helpers';

const pickSelectedOptions = (filters: TFilterPayload, isExcluded: boolean): ICompanyOption[] => {
  const issuerIds = isExcluded ? filters.excludedIssuerIds : filters.includedIssuerIds;
  const parentIds = isExcluded ? filters.excludedParentIds : filters.includedParentIds;

  return (filters.selectedParentIssuerOptions ?? []).filter(({ id, type }) => (
    (type === 'issuer' ? issuerIds : parentIds).includes(id)
  ));
};

const mergeOptions = (primary: ICompanyOption[], extra: ICompanyOption[]): ICompanyOption[] => [
  ...primary,
  ...extra.filter(({ value }) => !primary.some((option) => option.value === value)),
];

const useParentsIssuersSection = () => {
  const { localFilters, setLocalFilters } = useCorporateBondScreenerSidebarFilterContext();
  const repository = getContainer().get<BondScreenersRepository>(BondScreenersRepository);
  const { isExcluded, onToggleExclude: baseToggleExclude } = useExcludeToggle({
    includeKey: ['includedIssuerIds', 'includedParentIds'],
    excludeKey: ['excludedIssuerIds', 'excludedParentIds'],
    stateKey: 'isParentsIssuersExcluded',
    useFilterContext: useCorporateBondScreenerSidebarFilterContext,
  });

  const issuerKey = isExcluded ? 'excludedIssuerIds' : 'includedIssuerIds';
  const parentKey = isExcluded ? 'excludedParentIds' : 'includedParentIds';

  const [selectedOptions, setSelectedOptions] = useState<ICompanyOption[]>(
    () => pickSelectedOptions(localFilters, isExcluded),
  );
  const latestSelection = useRef(selectedOptions);
  const [currentOptions, setCurrentOptions] = useState<IBondScreenerCompanyGroup[]>([]);

  const handleChange = useCallback((selected: ICompanyOption[]): void => {
    latestSelection.current = selected;
    setSelectedOptions(selected);
    setLocalFilters((prev) => ({
      ...prev,
      selectedParentIssuerOptions: selected,
      [issuerKey]: selected.filter((option) => option.type === 'issuer').map((option) => option.id),
      [parentKey]: selected.filter((option) => option.type === 'parent').map((option) => option.id),
    }));
  }, [setLocalFilters, issuerKey, parentKey]);

  useEffect(() => {
    const issuerIds = localFilters[issuerKey];
    const parentIds = localFilters[parentKey];
    if (selectedOptions.length === issuerIds.length + parentIds.length) {
      return undefined;
    }

    let isMounted = true;

    const lookUpOptions = async (): Promise<ICompanyOption[]> => {
      try {
        const { issuers, parents } = await repository.getCompanyList({ includedCompanyIds: [...issuerIds, ...parentIds] });

        return [
          ...toOptions(issuers.filter(({ id }) => issuerIds.includes(id)), 'issuer'),
          ...toOptions(parents.filter(({ id }) => parentIds.includes(id)), 'parent'),
        ];
      } catch (error) {
        Logger.error({ message: 'Failed to resolve initial company names for ParentsIssuers filter', error });

        return [
          ...toOptions(issuerIds.map((id) => ({ id, name: id })), 'issuer'),
          ...toOptions(parentIds.map((id) => ({ id, name: id })), 'parent'),
        ];
      }
    };

    const resolveInitialOptions = async (): Promise<void> => {
      const found = await lookUpOptions();
      if (isMounted) {
        handleChange(mergeOptions(found, latestSelection.current));
      }
    };

    resolveInitialOptions();

    return () => {
      isMounted = false;
    };
  }, []);

  const onToggleExclude = useCallback(() => {
    baseToggleExclude();
    setLocalFilters((prev) => ({
      ...prev,
      issuedByHoldingCompany: prev.issuedByHoldingCompany !== null ? !prev.issuedByHoldingCompany : null,
      issuedByOperatingCompany: prev.issuedByOperatingCompany !== null ? !prev.issuedByOperatingCompany : null,
    }));
  }, [baseToggleExclude, setLocalFilters]);

  const handleHoldCoChange = useCallback((): void => {
    setLocalFilters((prev) => ({
      ...prev,
      issuedByHoldingCompany: prev.issuedByHoldingCompany !== null ? null : !isExcluded,
    }));
  }, [setLocalFilters, isExcluded]);

  const handleOpCoChange = useCallback((): void => {
    setLocalFilters((prev) => ({
      ...prev,
      issuedByOperatingCompany: prev.issuedByOperatingCompany !== null ? null : !isExcluded,
    }));
  }, [setLocalFilters, isExcluded]);

  const loadOptions = useCallback(async (
    query: string,
    callback: (groups: IBondScreenerCompanyGroup[]) => void,
  ) => {
    const trimmedQuery = query.trim();
    if (trimmedQuery.length < MIN_QUERY_LENGTH.company) {
      callback([]);
      return [];
    }
    setCurrentOptions([]);
    try {
      const { issuers: issuerList, parents: parentList } = await repository.getCompanyList({ search: trimmedQuery });
      const options: IBondScreenerCompanyGroup[] = [
        { label: 'Issuers', options: toOptions(issuerList, 'issuer') },
        { label: 'Parents', options: toOptions(parentList, 'parent') },
      ];
      setCurrentOptions(options);
      callback(options);
      return options;
    } catch (error) {
      Logger.error({ message: 'Failed to load company list', error });
      AlertManager.error('Failed to load companies');
      callback([]);
      return [];
    }
  }, [repository]);

  return {
    loadOptions,
    selectedOptions,
    handleChange,
    currentOptions,
    isExcluded,
    onToggleExclude,
    isHoldCoChecked: localFilters.issuedByHoldingCompany !== null,
    isOpCoChecked: localFilters.issuedByOperatingCompany !== null,
    handleHoldCoChange,
    handleOpCoChange,
  };
};

export default useParentsIssuersSection;
